// MVP 3.0 "report a problem" — the candidate-facing preset categories.
//
// Keys MUST stay in sync with the backend whitelist
// (backend/api/candidate.py `PROBLEM_REPORT_CATEGORIES`): the backend validates
// membership, this file owns the human labels. Adding/removing a category is a
// two-file change (here + that frozenset) with NO migration — categories are
// stored as a JSONB array of these keys, never a PG enum. The recruiter view
// maps stored keys back to labels via `problemCategoryLabel`.

export interface ProblemCategory {
  key: string;
  // Full label shown to the candidate on each checkbox row.
  label: string;
  // Compact label for the recruiter's chips (the full label is too long).
  shortLabel: string;
}

export const PROBLEM_CATEGORIES: ReadonlyArray<ProblemCategory> = [
  {
    key: "technical",
    label: "Technical issue (page froze, or the editor / Run button wasn't working)",
    shortLabel: "Technical issue",
  },
  {
    key: "question",
    label: "A question seems unclear or contains an error",
    shortLabel: "Question error",
  },
  { key: "timer", label: "Timer / time-limit problem", shortLabel: "Timer" },
  {
    key: "connection",
    label: "Internet connection problem",
    shortLabel: "Connection",
  },
  { key: "camera", label: "Camera / video issue", shortLabel: "Camera" },
  { key: "other", label: "Other", shortLabel: "Other" },
];

// Mirrors the backend's `PROBLEM_REPORT_DESCRIPTION_MAX`; used as the textarea
// `maxLength` so the candidate can't exceed what the API would 422 on.
export const PROBLEM_DESCRIPTION_MAX = 2000;

const LABEL_BY_KEY = new Map(PROBLEM_CATEGORIES.map((c) => [c.key, c.label]));
const SHORT_LABEL_BY_KEY = new Map(
  PROBLEM_CATEGORIES.map((c) => [c.key, c.shortLabel]),
);

// Stored category key → human label. Falls back to the raw key so a key the FE
// doesn't recognize yet (e.g. after a backend-only addition) still renders.
export function problemCategoryLabel(key: string): string {
  return LABEL_BY_KEY.get(key) ?? key;
}

// Stored category key → compact label for recruiter chips.
export function problemCategoryShortLabel(key: string): string {
  return SHORT_LABEL_BY_KEY.get(key) ?? key;
}
